import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { correctionsApi } from '../../api/corrections'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { STATUS_TONE, STATUS_WORDS } from '../../lib/improve'
import { CorrectionThread } from './CorrectionThread'
import { usePresets } from './usePresets'

// What has already been corrected here, what re-reading the evidence found, and where the nightly loop got
// to with it. The audit trail behind what the row now says — a correction is inspectable, never a silent
// overwrite — and the door back into the discussion that went with it.

export function CorrectionHistory({ entityId }: { entityId: string }) {
  const spoke = useSpoke()
  const presets = usePresets()
  const [discussing, setDiscussing] = useState<string | null>(null)
  const q = useQuery({ queryKey: ['corrections', spoke, entityId], queryFn: () => correctionsApi.forEntity(spoke, entityId), staleTime: 30_000 })
  const list = q.data?.corrections ?? []
  if (list.length === 0) return null

  // the server owns the vocabulary, so a stored value reads as the words the person clicked
  const words = (kind: string, value: string | null): string | null => {
    if (!value) return null
    return presets.data?.kinds.find((k) => k.kind === kind)?.presets.find((p) => p.value === value)?.label ?? value
  }

  return (
    <div className="correction-history">
      <h3>What you corrected</h3>
      <ul className="plain">
        {list.map((c) => (
          <li key={c.correctionId}>
            <strong>{words(c.entityKind, c.toValue)}</strong>
            <span className="muted small">
              {c.fromValue ? `was ${words(c.entityKind, c.fromValue)} · ` : ''}{formatDate(c.at)}
              {' · '}<span className={`tag sig ${STATUS_TONE[c.status]}`}>{STATUS_WORDS[c.status]}</span>
            </span>
            <span className="correction-why">{c.reason}</span>
            {c.investigation && <details className="correction-dig"><summary>What Scout found when it re-read the evidence</summary><p>{c.investigation}</p></details>}
            <button className="link-button small" onClick={() => setDiscussing(discussing === c.correctionId ? null : c.correctionId)}>
              {discussing === c.correctionId ? 'Hide the discussion' : c.turnCount > 0 ? `Discussion · ${c.turnCount} turn${c.turnCount === 1 ? '' : 's'}` : 'Discuss it'}
            </button>
            {discussing === c.correctionId && <CorrectionThread correctionId={c.correctionId} compact />}
          </li>
        ))}
      </ul>
    </div>
  )
}
